import React, { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import './style.less';
import { Icon } from 'semantic-ui-react';
import UserContext from '../../../contexts/UserContext';
import { Registrant } from '../../types/Registrant';
import { listResidentById } from '../../services/Resident';
import sendToast from '../../../utils/Toast';
import PostsFeed from '../../components/Posts/PostsFeed';
import FeedView from '../../components/Social/FeedView';
import GroupsPanel from '../../components/Social/GroupsPanel';
import GroupDocumentsWidget from '../../components/Social/GroupDocumentsWidget';
import BirthdaysSection from '../../components/Social/BirthdaysSection';
import DirectorySection from '../../components/Social/DirectorySection';
import ResidentDetailModal from '../../components/Social/ResidentDetailModal';
import { Group } from '../../types/Group';
import { fetchAllResidentGroups } from '../../services/Groups';
import { normalizeId } from '../../../utils/normalizeId';
import { getResidentDisplayFirstName, getResidentDisplayImage, getResidentDisplayLastName } from '../../utils/residentIdentity';

type CenterTab = 'feed' | 'posts';

const ResidentSocial = () => {
    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]];

    const [centerTab, setCenterTab] = useState<CenterTab>('posts');
    const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedResident, setSelectedResident] = useState<Registrant | null>(null);
    // Posts store group ids only; keep a name lookup so post cards can show a group tag.
    const [groupNameById, setGroupNameById] = useState<Record<string, string>>({});
    const [groupNamesLoading, setGroupNamesLoading] = useState(true);

    // Semantic's `.ui.segment.pushable` is an `overflow-y: auto` box, which would
    // capture the sticky sidebars and stop them tracking the page scroll.
    // Modifier class is toggled here rather than using :has() for older browsers.
    useLayoutEffect(() => {
        const shell = document.querySelector('.neighbors-interior-shell');
        if (!shell) return undefined;
        shell.classList.add('neighbors-interior-shell--social');
        return () => {
            shell.classList.remove('neighbors-interior-shell--social');
        };
    }, []);

    const activeFacilityId = activeResident?.Facility;
    const activeResidentId = activeResident?._id;

    // Built from its own unpaginated fetch rather than from GroupsPanel's first
    // page, otherwise a post tagged to a group past that page renders untagged.
    useEffect(() => {
        if (!activeFacilityId || !activeResidentId) {
            setGroupNamesLoading(false);
            return undefined;
        }
        let cancelled = false;
        setGroupNamesLoading(true);
        (async () => {
            try {
                const groups = await fetchAllResidentGroups(activeFacilityId, String(activeResidentId));
                if (cancelled) return;
                const nameById: Record<string, string> = {};
                (groups || []).forEach(group => {
                    const groupId = normalizeId(group._id);
                    if (groupId && group.Name) nameById[groupId] = group.Name;
                });
                setGroupNameById(nameById);
            } catch {
                // The tag is decorative; GroupsPanel already surfaces group load errors.
            } finally {
                if (!cancelled) setGroupNamesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [activeFacilityId, activeResidentId]);

    const handleProfileClick = async (registrantId: string) => {
        try {
            setSelectedResident(null);
            setModalLoading(true);
            setShowModal(true);
            const resident = await listResidentById(activeResident.Facility, registrantId);
            if (resident) {
                setSelectedResident(resident);
            } else {
                setShowModal(false);
                sendToast('warn', 'Resident not found');
            }
        } catch (error) {
            setShowModal(false);
            sendToast('error', error instanceof Error ? error.message : 'Error fetching resident details');
        } finally {
            setModalLoading(false);
        }
    };

    const handleDirectoryProfileClick = (resident: Registrant) => {
        setSelectedResident(resident);
        setShowModal(true);
    };

    const handleMentionClick = async (type: 'resident' | 'staff', id: string) => {
        if (type !== 'resident') return;
        await handleProfileClick(id);
    };

    const handleGroupSelect = (group: Group | null) => {
        setSelectedGroup(group);
    };

    const selectedGroupId = useMemo(() => normalizeId(selectedGroup?._id), [selectedGroup]);
    const groupResidentIds = useMemo(() => {
        const rawResidentIds = selectedGroup?.ResidentIds || selectedGroup?.Residents?.map(r => r._id) || [];
        return rawResidentIds.map(normalizeId).filter(Boolean);
    }, [selectedGroup]);
    const groupStaffIds = useMemo(() => {
        const rawStaffIds = selectedGroup?.StaffIds || selectedGroup?.Staff?.map(s => s._id) || [];
        return rawStaffIds.map(normalizeId).filter(Boolean);
    }, [selectedGroup]);
    const selectedGroupIds = useMemo(
        () => (selectedGroupId ? [selectedGroupId] : []),
        [selectedGroupId],
    );

    return (
        <div className="social-page mx-auto grid w-full max-w-social-page items-start gap-nbp-16 px-nbp-8 pt-nbp-16 pb-nbp-48 font-nb-body md:px-nbp-16 md:pt-nbp-24 md:pb-nbp-64 xl:grid-cols-social xl:px-nbp-20 2xl:gap-nbp-24 2xl:px-nbp-36 2xl:pt-nbp-32 2xl:pb-nbp-80">
            {/* Left: Groups + Group Documents.
                Sticky from `xl` up, where the three-column grid exists; stacked
                below that, and paired two-up on tablets so the feed isn't pushed
                a full screen down. */}
            <div className="social-groups-panel grid min-w-0 grid-cols-1 gap-nbp-16 md:grid-cols-2 xl:sticky xl:top-nbp-104 xl:grid-cols-1">
                <GroupsPanel
                    facilityId={activeResident.Facility}
                    residentId={activeResident._id}
                    selectedGroupId={selectedGroupId || null}
                    onSelectGroup={handleGroupSelect}
                />
                <GroupDocumentsWidget
                    facilityId={activeResident.Facility}
                    residentId={String(activeResident._id)}
                    selectedGroup={selectedGroup}
                />
            </div>

            {/* Center: Feed/Posts */}
            <div className="social-center-panel flex min-w-0 flex-col gap-nbp-20">
                <div className="center-header-bar">
                    <div
                        className={`center-header-tab ${centerTab === 'posts' ? 'active' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setCenterTab('posts')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCenterTab('posts');
                            }
                        }}
                    >
                        <Icon name="pencil" />
                        <span>Posts</span>
                    </div>
                    <div
                        className={`center-header-tab ${centerTab === 'feed' ? 'active' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => setCenterTab('feed')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCenterTab('feed');
                            }
                        }}
                    >
                        <Icon name="user" />
                        <span>Your Feed</span>
                    </div>
                </div>
                <div className="social-tab-content">
                    {/* Both stay mounted (hidden via CSS rather than unmounted) so
                        switching tabs doesn't lose an in-progress post draft. */}
                    <div style={{ display: centerTab === 'feed' ? 'block' : 'none' }}>
                        <FeedView
                            facilityId={activeResident.Facility}
                            currentResidentId={activeResident._id}
                            groupResidentIds={groupResidentIds}
                            groupStaffIds={groupStaffIds}
                            selectedGroupIds={selectedGroupIds}
                            onProfileClick={handleProfileClick}
                            isActive={centerTab === 'feed'}
                        />
                    </div>
                    <div style={{ display: centerTab === 'posts' ? 'block' : 'none' }}>
                        <PostsFeed
                            facilityId={activeResident.Facility}
                            currentResidentId={activeResident._id}
                            groupResidentIds={groupResidentIds}
                            groupStaffIds={groupStaffIds}
                            selectedGroupIds={selectedGroupIds}
                            onMentionClick={handleMentionClick}
                            composerFirstName={getResidentDisplayFirstName(activeResident)}
                            composerLastName={getResidentDisplayLastName(activeResident)}
                            composerImage={getResidentDisplayImage(activeResident)}
                            groupNameById={groupNameById}
                            groupsLoading={groupNamesLoading}
                            isActive={centerTab === 'posts'}
                        />
                    </div>
                </div>
            </div>

            {/* Right: Birthdays + Directory */}
            <div className="social-right-sidebar grid min-w-0 grid-cols-1 gap-nbp-16 xl:sticky xl:top-nbp-104">
                <BirthdaysSection facilityId={activeResident.Facility} groupResidentIds={groupResidentIds} onProfileClick={handleProfileClick} />
                <DirectorySection
                    facilityId={activeResident.Facility}
                    residentId={String(activeResident._id)}
                    groupResidentIds={groupResidentIds}
                    onProfileClick={handleDirectoryProfileClick}
                />
            </div>

            <ResidentDetailModal
                open={showModal}
                loading={modalLoading}
                onClose={() => setShowModal(false)}
                resident={selectedResident}
            />
        </div>
    );
};

export default ResidentSocial;
